import axios from 'axios';

// In production, REACT_APP_API_URL must be set to Railway backend URL
// e.g. https://your-app.up.railway.app/api
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true
});

// Add auth token to requests if it exists
api.interceptors.request.use(config => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle token expiration and subscription gates
api.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        if (error.response?.status === 403 && error.response?.data?.error === 'subscription_required') {
            window.location.href = '/subscription?upgrade=true';
        }
        if (error.response?.status === 403 && error.response?.data?.error === 'subscription_expired') {
            window.location.href = '/subscription?expired=true';
        }
        return Promise.reject(error);
    }
);

// Badge and Leaderboard endpoints
api.getBadges = async () => {
    const response = await api.get('/users/badges');
    return response.data;
};

api.getLeaderboard = async (category = null) => {
    const endpoint = category ? `/users/badges/leaderboard/${category}` : '/users/badges/leaderboard';
    const response = await api.get(endpoint);
    return response.data;
};

export const authAPI = {
    register: async (username, email, password, options = {}) => {
        const response = await api.post('/auth/register', {
            username,
            email,
            password,
            marketingConsent: options.marketingConsent || false,
            attribution: options.attribution || null,
        });
        return response.data;
    },

    login: async (email, password) => {
        const response = await api.post('/auth/login', {
            email,
            password
        });
        return response.data;
    },

    logout: async () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    },

    getProfile: async () => {
        const response = await api.get('/auth/profile');
        return response.data;
    },

    updateProfile: async (profileData) => {
        const response = await api.patch('/auth/profile', profileData);
        return response.data;
    },

    uploadAvatar: async (file) => {
        const formData = new FormData();
        formData.append('avatar', file);
        
        const response = await api.post('/auth/upload-avatar', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    },

    checkHandleEligibility: async () => {
        const response = await api.get('/auth/handle/eligibility');
        return response.data;
    },

    unlockHandleChange: async () => {
        const response = await api.post('/auth/handle/unlock');
        return response.data;
    },

    confirmHandleUnlock: async (sessionId) => {
        const response = await api.post(`/auth/handle/confirm-unlock?session_id=${sessionId}`);
        return response.data;
    },

    changeHandle: async (newHandle) => {
        const response = await api.post('/auth/handle/change', { newHandle });
        return response.data;
    }
};

export const subscriptionAPI = {
    getPlans: async () => {
        const response = await api.get('/subscription/plans');
        return response.data;
    },

    createCheckoutSession: async (planType, billingCycle, trialUpgradePlan = null, teamSize = null) => {
        const payload = {
            planType,
            billingCycle,
            ...(trialUpgradePlan && { trialUpgradePlan }),
            ...(teamSize && { teamSize })
        };
        
        const response = await api.post('/subscription/create-checkout-session', payload);
        return response.data;
    },

    getCurrentSubscription: async () => {
        const response = await api.get('/subscription/current');
        return response.data;
    },

    cancelSubscription: async () => {
        const response = await api.post('/subscription/cancel');
        return response.data;
    }
};

export const adminAPI = {
    // Get dashboard stats (users, subscriptions, revenue)
    getStats: async () => {
        const response = await api.get('/admin/stats');
        return response.data;
    },

    // Get all users (with pagination)
    getUsers: async (page = 1, limit = 10) => {
        const response = await api.get(`/admin/users?page=${page}&limit=${limit}`);
        return response.data;
    },

    // Get a specific user by ID
    getUser: async (userId) => {
        const response = await api.get(`/admin/users/${userId}`);
        return response.data;
    },

    // Update a user's subscription status (for testing purposes)
    updateUserSubscription: async (userId, subscriptionData) => {
        const response = await api.put(`/admin/users/${userId}/subscription`, subscriptionData);
        return response.data;
    },

    // Coupon management
    createCoupons: async (data) => {
        const response = await api.post('/admin/coupons', data);
        return response.data;
    },

    getCoupons: async (status) => {
        const url = status ? `/admin/coupons?status=${status}` : '/admin/coupons';
        const response = await api.get(url);
        return response.data;
    },

    revokeCoupon: async (couponId) => {
        const response = await api.patch(`/admin/coupons/${couponId}/revoke`);
        return response.data;
    },

    // Email stats
    getEmailStats: async (days = 30) => {
        const response = await api.get(`/admin/email-stats?days=${days}`);
        return response.data;
    },

    // Email journey for a specific user
    getEmailJourney: async (userId) => {
        const response = await api.get(`/admin/email-journey/${userId}`);
        return response.data;
    },

    // FLEX social loop analytics
    getFlexStats: async (days = 30) => {
        const response = await api.get(`/admin/flex-stats?days=${days}`);
        return response.data;
    },

    // Speed test analytics
    getSpeedTestStats: async (days = 30) => {
        const response = await api.get(`/admin/speed-test-stats?days=${days}`);
        return response.data;
    },

    // Post-signup user journey
    getPostSignupJourney: async (days = 30) => {
        const response = await api.get(`/admin/post-signup-journey?days=${days}`);
        return response.data;
    },
};

export const couponAPI = {
    redeem: async (code) => {
        const response = await api.post('/coupon/redeem', { code });
        return response.data;
    }
};

export const affiliateAPI = {
    // Get current affiliate stats
    getStats: async () => {
        const response = await api.get('/affiliate/stats');
        return response.data;
    },
    
    // Get affiliate referral details
    getReferrals: async () => {
        const response = await api.get('/affiliate/referrals');
        return response.data;
    }
};

export const flexProfileAPI = {
    // Get public profile by handle
    getPublicProfile: async (handle) => {
        const response = await api.get(`/flex/profile/${handle}`);
        return response.data;
    },

    // Get current user's FLEX profile settings
    getSettings: async () => {
        const response = await api.get('/flex/profile/me/settings');
        return response.data;
    },

    // Update FLEX profile settings
    updateSettings: async (data) => {
        const response = await api.patch('/flex/profile/me/settings', data);
        return response.data;
    },

    // Get current user's cards for gallery management
    getMyCards: async () => {
        const response = await api.get('/flex/profile/me/cards');
        return response.data;
    },

    // Toggle card visibility
    setCardVisibility: async (cardId, visibility) => {
        const response = await api.patch(`/flex/profile/me/cards/${cardId}/visibility`, { visibility });
        return response.data;
    },
};

export const flexLeaderboardAPI = {
    getLeaderboard: async (sort = 'effectiveWpm', period = 'allTime', limit = 50) => {
        const response = await api.get(`/flex/leaderboards?sort=${sort}&period=${period}&limit=${limit}`);
        return response.data;
    },

    getMyRank: async () => {
        const response = await api.get('/flex/leaderboards/me');
        return response.data;
    },
};

export const flexFeedAPI = {
    getFeed: async (filter = 'global', page = 1, limit = 20) => {
        const response = await api.get(`/flex/feed?filter=${filter}&page=${page}&limit=${limit}`);
        return response.data;
    },

    createPost: async (data) => {
        const response = await api.post('/flex/feed', data);
        return response.data;
    },

    react: async (postId, reactionType) => {
        const response = await api.post(`/flex/feed/${postId}/react`, { reactionType });
        return response.data;
    },

    report: async (postId) => {
        const response = await api.post(`/flex/feed/${postId}/report`);
        return response.data;
    },

    delete: async (postId) => {
        const response = await api.delete(`/flex/feed/${postId}`);
        return response.data;
    },
};

export const flexGroupAPI = {
    create: async (data) => {
        const response = await api.post('/flex/groups', data);
        return response.data;
    },

    getMyGroups: async () => {
        const response = await api.get('/flex/groups/me');
        return response.data;
    },

    getGroup: async (groupId) => {
        const response = await api.get(`/flex/groups/${groupId}`);
        return response.data;
    },

    join: async (inviteCode) => {
        const response = await api.post('/flex/groups/join', { inviteCode });
        return response.data;
    },

    createChallenge: async (groupId, template) => {
        const response = await api.post(`/flex/groups/${groupId}/challenges`, { template });
        return response.data;
    },

    getLeaderboard: async (groupId) => {
        const response = await api.get(`/flex/groups/${groupId}/leaderboard`);
        return response.data;
    },

    leave: async (groupId) => {
        const response = await api.delete(`/flex/groups/${groupId}/leave`);
        return response.data;
    },
};

export const reviewBooksAPI = {
    getAll: async () => {
        const response = await api.get('/review-books');
        return response.data;
    },

    getAllAdmin: async () => {
        const response = await api.get('/review-books/admin/all');
        return response.data;
    },

    getBySlug: async (slug) => {
        const response = await api.get(`/review-books/${slug}`);
        return response.data;
    },

    create: async (data) => {
        const response = await api.post('/review-books', data);
        return response.data;
    },

    update: async (id, data) => {
        const response = await api.put(`/review-books/${id}`, data);
        return response.data;
    },

    delete: async (id) => {
        const response = await api.delete(`/review-books/${id}`);
        return response.data;
    },

    uploadCover: async (id, file) => {
        const formData = new FormData();
        formData.append('cover', file);
        const response = await api.post(`/review-books/${id}/cover`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

export default api;
